<template>
  <Head title="Orders" />

  <div class="mx-auto w-11/12 max-w-[1200px] py-6">
    <!-- Search + filter card -->
    <div class="rounded-t-lg border border-neutral-200 bg-white px-4 py-3">
      <!-- Search input -->
      <div class="relative">
        <i
          class="pi pi-search absolute top-1/2 left-3 -translate-y-1/2 text-sm text-neutral-400"
        />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by file number, description, or officer..."
          class="text-navy-800 w-full rounded-lg border border-neutral-200 bg-neutral-50 py-2 pr-3 pl-9 text-[13px] transition-colors outline-none placeholder:text-neutral-400 focus:border-green-500 focus:bg-white"
          @input="debouncedSearch"
        />
      </div>

      <!-- Filter pills + sort -->
      <div class="mt-3 flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <button
            v-for="filter in statusFilters"
            :key="filter.value"
            class="cursor-pointer rounded-full border-none px-3 py-1 text-[12px] font-medium transition-colors"
            :class="
              activeStatus === filter.value
                ? 'bg-navy-50 text-navy-800 font-semibold'
                : 'bg-transparent text-neutral-400 hover:text-neutral-600'
            "
            @click="setStatusFilter(filter.value)"
          >
            {{ filter.label }}
            <span
              v-if="filter.count !== undefined"
              class="ml-1"
              :class="
                activeStatus === filter.value
                  ? 'text-navy-500'
                  : 'text-neutral-300'
              "
            >
              {{ filter.count }}
            </span>
          </button>
        </div>

        <div class="relative">
          <button
            class="flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            @click="toggleSortMenu"
          >
            <i class="pi pi-sort-alt text-[11px]" />
            {{ sortOptions.find((o) => o.value === activeSort)?.label }}
          </button>
          <Menu ref="sortMenuRef" :model="sortMenuItems" :popup="true" />
        </div>
      </div>
    </div>

    <!-- Order list -->
    <div class="rounded-b-lg border border-t border-neutral-200 bg-white">
      <!-- Empty state -->
      <div
        v-if="ordersList.length === 0"
        class="flex flex-col items-center py-16 text-center"
      >
        <i class="pi pi-file-edit mb-4 text-4xl text-neutral-300" />
        <p class="text-navy-800 mb-1 text-sm font-semibold">
          {{
            searchQuery || activeStatus ? "No matching orders" : "No orders yet"
          }}
        </p>
        <p class="text-[13px] text-neutral-400">
          {{
            searchQuery || activeStatus
              ? "Try adjusting your search or filters"
              : "Create your first order to get started"
          }}
        </p>
      </div>

      <!-- Order rows -->
      <Link
        v-for="(order, i) in ordersList"
        :key="order.id"
        :href="`/orders/${order.gid}`"
        class="flex items-center justify-between px-4 py-3 no-underline transition-colors hover:bg-neutral-50"
        :class="{ 'border-b border-neutral-100': i < ordersList.length - 1 }"
      >
        <!-- Left: file number + status + description -->
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <span class="text-navy-800 text-[14px] font-semibold">
              {{ order.externalFileNumber || "No file number" }}
            </span>
            <StatusBadge :variant="getStatusDisplay(order.status).variant" dot>
              {{ getStatusDisplay(order.status).label }}
            </StatusBadge>
          </div>
          <p class="mt-0.5 truncate text-[12px] text-neutral-400">
            {{ order.displayHeadline || "Untitled Order" }}
          </p>
        </div>

        <!-- Right: updated + officer -->
        <span
          class="ml-4 w-[140px] shrink-0 text-right text-[12px] whitespace-nowrap text-neutral-400"
        >
          {{
            formatDistanceToNow(new Date(order.updatedAt), { addSuffix: true })
          }}
        </span>
        <div
          v-if="order.officer"
          class="ml-4 flex w-[120px] shrink-0 items-center gap-2"
        >
          <div
            class="from-navy-800 to-navy-600 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-[9px] font-bold text-white"
          >
            {{ getOfficerInitials(order.officer.name) }}
          </div>
          <span class="truncate text-[12px] text-neutral-500">
            {{ order.officer.name }}
          </span>
        </div>
      </Link>
    </div>

    <!-- Footer count -->
    <div v-if="ordersList.length > 0" class="mt-3 text-[12px] text-neutral-400">
      <template v-if="activeStatus || searchQuery">
        {{ ordersList.length }} of {{ totalOrderCount }} orders
      </template>
      <template v-else>
        {{ ordersList.length }}
        {{ ordersList.length === 1 ? "order" : "orders" }}
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Head, Link, router, usePage } from "@inertiajs/vue3"
import { formatDistanceToNow } from "date-fns"
import { computed, ref } from "vue"

import Menu from "primevue/menu"
import StatusBadge from "../../components/ui/StatusBadge.vue"
import { useOrderStatus } from "../../composables/useOrderStatus"

const { getStatusDisplay } = useOrderStatus()

interface OfficerType {
  id: string
  name: string
}

interface OrderNode {
  id: string
  gid: string
  displayHeadline: string | null
  status:
    | "DRAFT"
    | "CLOSING"
    | "UPLOADING"
    | "FINALIZING"
    | "COMPLETED"
    | "CANCELED"
  externalFileNumber: string | null
  updatedAt: string
  officer: OfficerType
}

interface OrdersConnection {
  nodes: OrderNode[]
}

interface CompanyType {
  id: string
  name: string
}

const props = defineProps<{
  company: CompanyType
  orders: OrdersConnection
  orderStatusCounts: Record<string, number>
}>()

const ordersList = computed(() => props.orders.nodes)

const totalOrderCount = computed(() => {
  return Object.values(props.orderStatusCounts).reduce((sum, n) => sum + n, 0)
})

// Get search/status from URL
const page = usePage()
const getParamFromUrl = (key: string): string => {
  const url = new URL(page.url, window.location.origin)
  return url.searchParams.get(key) || ""
}
const searchQuery = ref(getParamFromUrl("search"))
const activeStatus = ref(getParamFromUrl("status"))
const activeSort = ref(getParamFromUrl("sort") || "updated_at")

const sortOptions = [
  { label: "Last Updated", value: "updated_at" },
  { label: "Date Created", value: "created_at" },
  { label: "File Number", value: "file_number" },
]

const sortMenuRef = ref()
const toggleSortMenu = (event: Event) => sortMenuRef.value.toggle(event)
const sortMenuItems = computed(() =>
  sortOptions.map((opt) => ({
    label: opt.label,
    icon: activeSort.value === opt.value ? "pi pi-check" : undefined,
    command: () => {
      activeSort.value = opt.value
      navigateWithParams()
    },
  })),
)

const statusFilters = computed(() => {
  const counts = props.orderStatusCounts || {}
  return [
    { label: "All", value: "", count: totalOrderCount.value },
    { label: "Draft", value: "DRAFT", count: counts.DRAFT || 0 },
    { label: "Closing", value: "CLOSING", count: counts.CLOSING || 0 },
    { label: "Completed", value: "COMPLETED", count: counts.COMPLETED || 0 },
    { label: "Cancelled", value: "CANCELED", count: counts.CANCELED || 0 },
  ]
})

let searchTimeout: ReturnType<typeof setTimeout> | null = null

const debouncedSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    navigateWithParams()
  }, 300)
}

const setStatusFilter = (status: string) => {
  activeStatus.value = status
  navigateWithParams()
}

const navigateWithParams = () => {
  const params: Record<string, string> = {}
  if (searchQuery.value) params.search = searchQuery.value
  if (activeStatus.value) params.status = activeStatus.value
  if (activeSort.value && activeSort.value !== "updated_at")
    params.sort = activeSort.value

  router.get(`/companies/${props.company.id}/orders`, params, {
    preserveState: true,
    replace: true,
  })
}

const getOfficerInitials = (name: string) => {
  const parts = name.split(" ").filter(Boolean)
  if (parts.length >= 2) {
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}
</script>
